import { useState } from 'react';
import type { TrendPoint } from '@marstein-radar/shared';

// Malý viacnásobok (small multiples) namiesto jedného grafu s 3 nezávisle
// normalizovanými sériami (user feedback 2026-08-03: "tento graf je maximálne
// neprehľadný" — pôvodná verzia miešala tržby/zisk/Z"-skóre v jednej škále 0-1,
// takže tvar čiary nehovoril nič o skutočnej hodnote bez hoveru na presný bod).
// Každá metrika má teraz vlastnú mini-kartu s viditeľnou aktuálnou hodnotou,
// medziročnou zmenou a sparklinou vo vlastnej mierke — nič netreba hoverovať,
// aby si videl, čo graf hovorí.
const SPARK_WIDTH = 220;
const SPARK_HEIGHT = 56;
const SPARK_PAD = 6;

interface Series {
  key: 'revenue' | 'netIncome' | 'altmanZScore';
  label: string;
  className: string;
}

const SERIES: Series[] = [
  { key: 'revenue', label: 'Tržby', className: 'trend-line-revenue' },
  { key: 'netIncome', label: 'Výsledok hospodárenia', className: 'trend-line-income' },
  { key: 'altmanZScore', label: 'Altman Z"-skóre', className: 'trend-line-z' },
];

function formatValue(key: Series['key'], v: number): string {
  if (key === 'altmanZScore') return v.toFixed(2);
  return v.toLocaleString('sk-SK', { maximumFractionDigits: 0 }) + ' €';
}

function formatDelta(key: Series['key'], from: number, to: number): { text: string; dir: 'up' | 'down' | 'flat' } {
  const diff = to - from;
  if (Math.abs(diff) < 1e-9) return { text: '± 0', dir: 'flat' };
  const dir = diff > 0 ? 'up' : 'down';
  if (key === 'altmanZScore') {
    return { text: `${diff > 0 ? '+' : ''}${diff.toFixed(2)}`, dir };
  }
  if (from === 0) return { text: dir === 'up' ? '+100 %' : '−100 %', dir };
  const pct = (diff / Math.abs(from)) * 100;
  return { text: `${pct > 0 ? '+' : ''}${pct.toFixed(0)} %`, dir };
}

interface Props {
  points: TrendPoint[]; // chronologicky (najstarší → najnovší)
}

function TrendMiniCard({ series, points }: { series: Series; points: TrendPoint[] }): React.JSX.Element | null {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const n = points.length;
  const values = points.map((p) => p[series.key]);
  const known = values
    .map((v, i) => (v == null ? null : { i, v }))
    .filter((x): x is { i: number; v: number } => x !== null);
  if (known.length < 2) return null;

  const min = Math.min(...known.map((k) => k.v));
  const max = Math.max(...known.map((k) => k.v));
  const range = max - min || 1;
  const toXY = (i: number, v: number) => ({
    x: (i / (n - 1)) * SPARK_WIDTH,
    y: SPARK_HEIGHT - SPARK_PAD - ((v - min) / range) * (SPARK_HEIGHT - SPARK_PAD * 2),
  });
  const linePath = known
    .map(({ i, v }, idx) => {
      const { x, y } = toXY(i, v);
      return `${idx === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');
  const areaPath = `${linePath} L${toXY(known[known.length - 1]!.i, known[known.length - 1]!.v).x.toFixed(1)},${SPARK_HEIGHT} L${toXY(known[0]!.i, known[0]!.v).x.toFixed(1)},${SPARK_HEIGHT} Z`;

  const lastKnown = known[known.length - 1]!;
  const prevKnown = known.length > 1 ? known[known.length - 2]! : null;
  const delta = prevKnown ? formatDelta(series.key, prevKnown.v, lastKnown.v) : null;
  const hovered = hoverIdx != null ? known.find((k) => k.i === hoverIdx) : null;
  const displayed = hovered ?? lastKnown;

  function handleMove(e: React.MouseEvent<SVGSVGElement>): void {
    const rect = e.currentTarget.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * SPARK_WIDTH;
    const idx = Math.max(0, Math.min(n - 1, Math.round((relX / SPARK_WIDTH) * (n - 1))));
    // Najbližší známy bod k pozícii kurzora, nie surový index (chýbajúce roky).
    const nearest = known.reduce((best, k) => (Math.abs(k.i - idx) < Math.abs(best.i - idx) ? k : best));
    setHoverIdx(nearest.i);
  }

  return (
    <div className="trend-mini-card">
      <div className="trend-mini-head">
        <span className="trend-mini-label">
          <span className={`trend-legend-dot ${series.className}-dot`} />
          {series.label}
        </span>
        {delta && (
          <span className={`trend-mini-delta trend-mini-delta--${delta.dir}`}>{delta.text}</span>
        )}
      </div>
      <div className="trend-mini-value num">
        {formatValue(series.key, displayed.v)}
        <span className="trend-mini-period">{points[displayed.i]!.period}</span>
      </div>
      <svg
        className="spark trend-mini-spark"
        width="100%"
        height={SPARK_HEIGHT}
        viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
        preserveAspectRatio="none"
        onMouseMove={handleMove}
        onMouseLeave={() => setHoverIdx(null)}
      >
        <path className={`${series.className}-area`} d={areaPath} />
        <path className={series.className} d={linePath} />
        {known.map(({ i, v }) => {
          const { x, y } = toXY(i, v);
          const isDisplayed = i === displayed.i;
          return (
            <circle
              key={i}
              className={`${series.className}-dot`}
              cx={x}
              cy={y}
              r={isDisplayed ? 3.5 : 2}
              opacity={isDisplayed ? 1 : 0.5}
            />
          );
        })}
      </svg>
      <div className="trend-mini-range">
        <span>{points[known[0]!.i]!.period}</span>
        <span>{points[known[known.length - 1]!.i]!.period}</span>
      </div>
    </div>
  );
}

export function TrendChart({ points }: Props): React.JSX.Element | null {
  if (points.length < 2) return null;
  const cards = SERIES.map((series) => (
    <TrendMiniCard key={series.key} series={series} points={points} />
  )).filter(Boolean);
  if (cards.length === 0) return null;
  return <div className="trend-multi">{cards}</div>;
}
